import React, { useState, useEffect, useRef, useCallback } from 'react'
import { connect } from 'react-redux'
import { isBefore } from 'date-fns'

import AddEditAuditForm from '../components/AddEditAuditForm'
import { saveNewAudit, loadNewAudit, editAudit } from '../actions/addEditAuditAction'
import { getActiveAudits } from '../actions/searchAuditsAction'
import { parsePhone } from 'app/utils/fieldUtils'

const AddEditAuditContainer = ({ saveNewAudit, loadNewAudit, editAudit, getActiveAudits, auditRecord, audits, match, user, location }) => {
  const [values, setValues] = useState({
    auditingUnitCode: '',
    auditFiscalYear: '',
    auditTypeCode: '',
    organizationId: '',
    organizationEntityTypeId: '',
    auditLocationCode: '',
    auditNumber: '',
    notificationDate: undefined,
    closeOutDate: undefined,
    vaPocFirstName: '',
    vaPocMiddleInitial: '',
    vaPocLastName: '',
    vaPocEmail: '',
    vaPocPrimaryPhone: '',
    vaPocSecondaryPhone: '',
    orgPocFirstName: '',
    orgPocMiddleInitial: '',
    orgPocLastName: '',
    orgPocEmail: '',
    orgPocAddressOne: '',
    orgPocAddressTwo: '',
    orgPocCity: '',
    orgPocStateCode: '',
    orgPocZip: '',
    orgPocPrimaryPhone: '',
    orgPocSecondaryPhone: ''
  })
  const [errors, setErrors] = useState({})
  const [dateErrors, setDateErrors] = useState({})
  const [canCreateAll, setCanCreateAll] = useState(false)
  const focusRef = useRef(0)

  const loadExistingAudit = useCallback(
    (audit) => {
      for (const property in audit) {
        if (audit[property] === null) {
          audit[property] = !property.endsWith('Date') ? '' : undefined
        }
      }
      setValues({ ...audit, notificationDate: new Date(audit.notificationDate), closeOutDate: new Date(audit.closeOutDate) })
    }, [])

  const handleReset = useCallback(
    () => {
      loadNewAudit()
      setErrors({})
      setDateErrors({})
      setValues({
        auditingUnitCode: '',
        auditFiscalYear: '',
        auditTypeCode: '',
        organizationId: '',
        organizationEntityTypeId: '',
        auditLocationCode: '',
        auditNumber: '',
        notificationDate: undefined,
        closeOutDate: undefined,
        vaPocFirstName: '',
        vaPocMiddleInitial: '',
        vaPocLastName: '',
        vaPocEmail: '',
        vaPocPrimaryPhone: '',
        vaPocSecondaryPhone: '',
        orgPocFirstName: '',
        orgPocMiddleInitial: '',
        orgPocLastName: '',
        orgPocEmail: '',
        orgPocAddressOne: '',
        orgPocAddressTwo: '',
        orgPocCity: '',
        orgPocStateCode: '',
        orgPocZip: '',
        orgPocPrimaryPhone: '',
        orgPocSecondaryPhone: ''
      })
      focusRef.current.focus()
      setAuditUnit(user.authorizations)
    }, [loadNewAudit, user.authorizations])

  useEffect(() => {
    if (auditRecord.success) {
      loadExistingAudit(auditRecord.audit)
    } else if (match.params.auditId && audits.length > 0) {
      loadExistingAudit(audits.find(audit => String(audit.id) === match.params.auditId))
    } 
  }, [match.params.auditId, auditRecord, audits, loadExistingAudit]) 

  useEffect(() => {
    return () => {
      getActiveAudits()
      handleReset()
    }
  }, [location, getActiveAudits, handleReset])

  useEffect(() => {
    if (user.authorizations.length > 0) {
      setAuditUnit(user.authorizations)
    }
  }, [location, user.authorizations])

  const setAuditUnit = (authorizations) => {
    if (authorizations.includes('Create MU Audit File') && authorizations.includes('Create QA Audit File')) {
      setCanCreateAll(true)
    } else if (authorizations.includes('Create MU Audit File')) {
      setValues(values => ({ ...values, auditingUnitCode: 'M' }))
    } else if (authorizations.includes('Create QA Audit File')) {
      setValues(values => ({ ...values, auditingUnitCode: 'Q' }))
    }
  }

  const handleChange = (event) => {
    let target = event.target

    if (target.name === 'auditFiscalYear' && target.value.length > 4) { target.value = target.value.slice(0, 4) }
    if (target.name === 'orgPocZip' && target.value.length > 5) { target.value = target.value.slice(0, 5) }
    if (target.name === 'organizationId' && target.value.length > 6) { target.value = target.value.slice(0, 6) }
    if (target.name === 'auditNumber' && target.value.length > 3) { target.value = target.value.slice(0, 3) }
    if (target.name.endsWith('Phone')) { target.value = parsePhone(target.value) }

    setValues({ ...values, [target.name]: target.value })

    if (target.name === 'auditTypeCode') {
      setValues(values => ({ ...values, organizationId: '', organizationEntityTypeId: '' }))
    }

    setErrors({})
  }

  const handleDateChange = (field) => (date) => {
    setDateErrors({})
    setValues({ ...values, [field]: date })
    if (field === 'closeOutDate' && isBefore(date, values.notificationDate)) {
      setDateErrors({ ...dateErrors, [field]: 'Close-Out Date must be after Notification Date' })
    }
    if (field === 'notificationDate' && isBefore(values.closeOutDate, date)) {
      setDateErrors({ ...dateErrors, [field]: 'Notification Date must be before Close-Out Date' })
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    if (!values.notificationDate || !values.closeOutDate) {
      handleInvalidDates()
    } else if (match.params.auditId) {
      editAudit(values, match.params.auditId)
      loadNewAudit()
    } else {
      saveNewAudit(values)
      loadNewAudit()
    }
  }

  // this is a workaround since react-datepicker doesn't accept an onInvalid prop
  const handleInvalidDates = () => {
    if (!values.notificationDate && !values.closeOutDate) {
      setDateErrors({ ...dateErrors, notificationDate: 'Required', closeOutDate: 'Required' })
    } else if (!values.closeOutDate) {
      setDateErrors({ ...dateErrors, closeOutDate: 'Required' })
    } else if (!values.notificationDate) {
      setDateErrors({ ...dateErrors, notificationDate: 'Required' })
    }
  }

  const handleInvalid = (event) => {
    event.preventDefault()
    let target = event.target
    if (values.notificationDate === undefined || values.closeOutDate === undefined) {
      handleInvalidDates()
    }
    setErrors({ ...errors, [target.name]: 'Required' })
  }

  return <AddEditAuditForm
    isEdit={!!match.params.auditId}
    handleChange={handleChange}
    handleDateChange={handleDateChange}
    handleSubmit={handleSubmit}
    handleInvalid={handleInvalid}
    handleReset={handleReset}
    auditRecord={auditRecord}
    canCreateAll={canCreateAll}
    errors={errors}
    dateErrors={dateErrors}
    focusRef={focusRef}
    {...values} />
}

const mapStateToProps = state => ({
  auditRecord: state.oversight.auditRecord,
  user: state.currentUser
})

const mapDispatchToProps = { saveNewAudit, loadNewAudit, editAudit, getActiveAudits }

export default connect(mapStateToProps, mapDispatchToProps)(AddEditAuditContainer)
